import React, { useEffect, useRef } from "react";
import { Button, CompactButton } from "@ogt/ui";

type IconName =
  | "busfront"
  | "car"
  | "zone:land-plot"
  | "pin"
  | "ruler"
  | "route";

type SidebarTriggerArgs = {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  id?: string;
  panelId?: string;
};

export interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
  id?: string;
  leadingIconName?: IconName;
  renderTrigger?: (args: SidebarTriggerArgs) => React.ReactNode;

  activeCount?: number;

  mobileTriggerHidden?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({
  title,
  isOpen,
  onToggle,
  children,
  id,
  leadingIconName,
  renderTrigger,
  activeCount = 0,
  mobileTriggerHidden = false,
  className,
  ...rest
}) => {
  const closeButtonWrapRef = useRef<HTMLDivElement>(null);
  const panelId = id ? `${id}-panel` : undefined;

  useEffect(() => {
    if (!isOpen) return;

    requestAnimationFrame(() => {
      const button = closeButtonWrapRef.current?.querySelector("button");

      if (button instanceof HTMLButtonElement) {
        button.focus();
      }
    });
  }, [isOpen]);

  return (
    <div
      className={[
        "sidebar-button-group max-w-20 md:max-w-full",
        mobileTriggerHidden
          ? "w-0 flex-none md:w-full"
          : "min-w-0 flex-1 md:w-full md:flex-none",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      data-sidebar-id={id}
      {...rest}
    >
      <div
        className={["relative w-full", mobileTriggerHidden && "hidden md:block"]
          .filter(Boolean)
          .join(" ")}
        data-active-count={activeCount}
      >
        {renderTrigger ? (
          renderTrigger({
            title,
            isOpen,
            onToggle,
            id,
            panelId,
          })
        ) : (
          <CompactButton
            id={id}
            type="button"
            onClick={onToggle}
            variant="secondary"
            showIcon
            direction="vertical"
            iconPosition="left"
            iconSize={22}
            size="small"
            iconName={leadingIconName}
            data-state={isOpen ? "open" : "closed"}
            className="w-full h-18 p-2! md:py-3! text-xs! justify-between!  md:h-auto  md:text-sm! md:justify-center!"
            aria-expanded={isOpen}
            aria-controls={panelId}
          >
            <span className="min-h-[30px] md:min-h-auto">{title}</span>
          </CompactButton>
        )}

        {activeCount > 0 && (
          <span
            aria-hidden="true"
            className={[
              "pointer-events-none",
              "absolute right-1 top-1 z-20",
              "flex min-h-5 min-w-5 items-center justify-center",
              "rounded-full px-1",
              "bg-[var(--button-primary-color-bg-active)] text-white",
              "text-[11px] font-bold leading-none border border-2 ",
            ].join(" ")}
          >
            {activeCount > 99 ? "99+" : activeCount}
          </span>
        )}
      </div>

      {isOpen && (
        <div
          id={panelId}
          className={[
            // mobile
            "fixed top-[0] left-0 right-0 z-40",
            "h-[calc(100dvh_-_90px)] w-full",
            "overflow-auto bg-[var(--box-color-bg)]",

            // desktop
            "md:absolute md:bottom-auto md:left-[112px] md:right-auto md:top-0",
            "md:h-full md:w-[400px]",
            "md:max-w-[calc(100vw_-_112px)]",

            "border-t md:border-t-0",
            "md:border-l md:border-r",
            "border-[var(--semantic-color-border-primary)]",
            "flex flex-col",
          ].join(" ")}
          role="region"
          aria-label={title}
        >
          <div
            className={[
              "sticky top-0 z-10",
              "flex justify-end",
              "bg-[var(--box-color-bg)]",
              "px-4 py-4",
            ].join(" ")}
          >
            <div ref={closeButtonWrapRef}>
              <Button
                type="button"
                variant="ghost"
                size="small"
                iconPosition="left"
                showIcon
                iconName="x"
                iconSize={20}
                onClick={onToggle}
                className="w-fit"
              >
                Stäng
              </Button>
            </div>
          </div>

          {children}
        </div>
      )}
    </div>
  );
};

export default Sidebar;
